<template>
  <div class="grc-risks-list">
    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <div
              class="d-flex align-items-center justify-content-between flex-wrap grc-gap-2"
            >
              <div>
                <h5 class="mb-0">Riscos</h5>
                <small class="text-muted"
                  >Cadastro e avaliação de riscos com score e vínculo a
                  requisitos.</small
                >
              </div>
              <div class="d-flex flex-wrap grc-gap-2">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="loading || exporting"
                  @click="exportCsv"
                >
                  {{ exporting ? "Exportando..." : "Exportar CSV" }}
                </b-button>
                <b-button
                  v-if="canCreate"
                  size="sm"
                  variant="primary"
                  @click="openNew"
                >
                  Novo risco
                </b-button>
              </div>
            </div>
          </template>
          <b-card-body>
            <div class="grc-filters-section">
              <b-form-group label="Pesquisar" class="grc-search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filters.search"
                    type="search"
                    placeholder="Título, evento ou categoria"
                    @keyup.enter="applyFilters"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filters.search" @click="clearSearch">
                      Limpar
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <b-form-group label="Status">
                <b-form-select
                  v-model="filters.status"
                  :options="statusFilterOptions"
                ></b-form-select>
              </b-form-group>
              <b-form-group label="Área">
                <b-form-select
                  v-model="filters.area"
                  :options="areaFilterOptions"
                ></b-form-select>
              </b-form-group>
              <b-button variant="outline-secondary" @click="applyFilters">
                Filtrar
              </b-button>
            </div>

            <b-alert
              v-if="activeStatusSummary"
              show
              variant="info"
              class="py-2"
            >
              Recorte do dashboard: {{ activeStatusSummary }}
            </b-alert>

            <GRCListTable
              :items="data.docs"
              :fields="fields"
              :busy="loading"
              :page="data.page"
              :total-rows="data.total"
              :per-page="data.limit"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              server-sorting
              empty-text="Nenhum risco encontrado"
              busy-text="Carregando riscos..."
              @sort-changed="onSortChanged"
              @page-change="changePage"
            >
              <template #cell(title)="slotData">
                <div>
                  <strong>{{ slotData.item.title }}</strong>
                  <div class="text-muted small">{{ slotData.item.event }}</div>
                </div>
              </template>
              <template #cell(area)="slotData">
                {{ slotData.item.area ? slotData.item.area.name : "-" }}
              </template>
              <template #cell(score)="slotData">
                <div>
                  <strong>{{ slotData.item.score }}</strong>
                  <div class="text-muted small">
                    {{ levelLabel(slotData.item.inherentLevel) }}
                  </div>
                </div>
              </template>
              <template #cell(status)="slotData">
                <b-badge :variant="statusVariant(slotData.item.status)">
                  {{ statusLabel(slotData.item.status) }}
                </b-badge>
              </template>
              <template #cell(treatmentStrategy)="slotData">
                {{ treatmentLabel(slotData.item.treatmentStrategy) }}
              </template>
              <template #cell(nextReviewAt)="slotData">
                {{ formatDate(slotData.item.nextReviewAt) }}
              </template>
              <template #cell(actions)="slotData">
                <div class="grc-action-buttons">
                  <b-button
                    v-if="canEdit"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--edit"
                    title="Editar"
                    aria-label="Editar"
                    @click="openEdit(slotData.item)"
                  >
                    <i class="fa fa-edit" aria-hidden="true"></i>
                  </b-button>
                  <b-button
                    v-if="
                      canApprove &&
                      getApprovalStatus(slotData.item) === 'pending'
                    "
                    size="sm"
                    variant="outline-success"
                    @click="approve(slotData.item)"
                  >
                    Aprovar
                  </b-button>
                  <b-button
                    v-if="
                      canApprove &&
                      getApprovalStatus(slotData.item) === 'pending'
                    "
                    size="sm"
                    variant="outline-warning"
                    @click="reject(slotData.item)"
                  >
                    Rejeitar
                  </b-button>
                  <b-button
                    v-if="canDelete"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--delete"
                    title="Inativar"
                    aria-label="Inativar"
                    @click="remove(slotData.item)"
                  >
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </b-button>
                </div>
              </template>
              <template #empty>
                <div class="text-muted text-center py-5">
                  <i class="fas fa-shield-alt fa-2x"></i>
                  <p class="mt-3 mb-0">Nenhum risco encontrado</p>
                </div>
              </template>
            </GRCListTable>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <GRCFormSidebar
      id="grc-risk-sidebar"
      :visible.sync="showSidebar"
      :title="selectedRisk ? 'Editar risco' : 'Novo risco'"
      @hidden="onModalHidden"
    >
      <GRCRiskForm
        :risk="selectedRisk"
        :area-options="areaOptions"
        :scope-options="scopeOptions"
        :project-options="projectOptions"
        :user-options="userOptions"
        :requirement-options="requirementOptions"
        :saving="saving"
        @submit="saveRisk"
        @cancel="showSidebar = false"
      />
    </GRCFormSidebar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getUsers } from "@/api/user";
import { getGrcMetadata } from "@/api/grc-metadata";
import { exportRowsToCsv } from "@/utils/csv-export";
import { getGrcRequirements } from "@/api/grc-requirements";
import {
  approveGrcRisk,
  createGrcRisk,
  deleteGrcRisk,
  getGrcRisk,
  getGrcRisks,
  rejectGrcRisk,
  updateGrcRisk,
} from "@/api/grc-risks";
import GRCFormSidebar from "./components/GRCFormSidebar.vue";
import GRCListTable from "./components/GRCListTable.vue";
import GRCRiskForm from "./GRCRiskForm.vue";

const STATUS = {
  OPEN: 1,
  WAITING_APPROVAL: 2,
  CLOSED: 3,
  IN_REVIEW: 4,
};

export default {
  name: "GRCRisksList",
  components: {
    GRCFormSidebar,
    GRCListTable,
    GRCRiskForm,
  },
  data() {
    return {
      loading: false,
      exporting: false,
      saving: false,
      sortBy: "score",
      sortDesc: true,
      showSidebar: false,
      selectedRisk: null,
      fields: [
        {
          key: "_id",
          label: "#",
          sortable: true,
          thStyle: { width: "90px !important" },
        },
        {
          key: "title",
          label: "Risco",
          sortable: true,
          thStyle: { "min-width": "240px !important" },
        },
        {
          key: "area",
          label: "Área",
          thStyle: { "min-width": "180px !important" },
        },
        {
          key: "score",
          label: "Score",
          sortable: true,
          thStyle: { width: "130px !important" },
        },
        {
          key: "status",
          label: "Status",
          sortable: true,
          thStyle: { width: "180px !important" },
        },
        {
          key: "treatmentStrategy",
          label: "Tratamento",
          sortable: true,
          thStyle: { width: "190px !important" },
        },
        {
          key: "nextReviewAt",
          label: "Próxima revisão",
          sortable: true,
          thStyle: { width: "170px !important" },
        },
        {
          key: "actions",
          label: "Ações",
          thStyle: { width: "220px !important" },
        },
      ],
      filters: {
        search: "",
        status: null,
        area: null,
      },
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 10,
      },
      areaOptions: [],
      scopeOptions: [],
      projectOptions: [],
      userOptions: [],
      requirementOptions: [],
      routeStatusValues: [],
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    currentCompanyId() {
      const companyId = parseInt(this.$route.query.company, 10);
      return Number.isNaN(companyId) ? null : companyId;
    },
    statusFilterOptions() {
      return [
        { value: null, text: "Todos" },
        { value: STATUS.OPEN, text: "Aberto" },
        { value: STATUS.WAITING_APPROVAL, text: "Aguardando aprovação" },
        { value: STATUS.CLOSED, text: "Fechado" },
        { value: STATUS.IN_REVIEW, text: "Em revisão" },
      ];
    },
    areaFilterOptions() {
      return [{ value: null, text: "Todas" }, ...this.areaOptions];
    },
    canCreate() {
      return this.hasRiskPermission("create");
    },
    canEdit() {
      return this.hasRiskPermission("edit");
    },
    canDelete() {
      return this.hasRiskPermission("delete");
    },
    canApprove() {
      return this.hasRiskPermission("approve");
    },
    activeStatusSummary() {
      if (this.routeStatusValues.length <= 1) {
        return "";
      }

      return this.routeStatusValues
        .map((value) => this.statusLabel(value))
        .join(", ");
    },
  },
  watch: {
    "$route.query": {
      async handler(newQuery, oldQuery) {
        const nextCompanyId = this.normalizeQueryNumber(newQuery.company);
        const previousCompanyId = this.normalizeQueryNumber(
          oldQuery && oldQuery.company
        );

        this.applyRouteState();

        if (nextCompanyId !== previousCompanyId) {
          await this.loadSupportData();
        }

        await this.loadRisks();
      },
    },
  },
  async created() {
    this.applyRouteState();
    await this.loadSupportData();
    await this.loadRisks();
  },
  methods: {
    normalizeQueryNumber(value) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    parseQueryNumberList(value) {
      if (value === null || value === undefined || value === "") {
        return [];
      }

      const rawValues = Array.isArray(value) ? value : [value];
      return Array.from(
        new Set(
          rawValues
            .flatMap((entry) => String(entry).split(","))
            .map((entry) => this.normalizeQueryNumber(entry))
            .filter(Boolean)
        )
      );
    },
    getRequestParams(extra = {}) {
      return {
        ...extra,
        company: this.currentCompanyId || undefined,
      };
    },
    getListParams(extra = {}) {
      return this.getRequestParams({
        page: this.data.page,
        size: this.data.limit,
        sortBy: this.sortBy || undefined,
        sortDesc: this.sortDesc,
        search: this.filters.search || undefined,
        status:
          this.routeStatusValues.length <= 1
            ? this.filters.status || undefined
            : undefined,
        statuses:
          this.routeStatusValues.length > 1
            ? this.routeStatusValues.join(",")
            : undefined,
        area: this.filters.area || undefined,
        ...extra,
      });
    },
    getMutationPayload(payload) {
      if (!this.currentCompanyId) {
        return payload;
      }

      return {
        ...payload,
        company: this.currentCompanyId,
      };
    },
    applyRouteState() {
      const query = this.$route.query || {};
      const statuses = this.parseQueryNumberList(
        query.statuses || query.status
      );

      this.filters.search =
        typeof query.search === "string" ? query.search : "";
      this.routeStatusValues = statuses;
      this.filters.status = statuses.length === 1 ? statuses[0] : null;
      this.filters.area = this.normalizeQueryNumber(query.area);
      this.data.page = this.normalizeQueryNumber(query.page) || 1;
    },
    buildRouteQuery(overrides = {}) {
      const search = Object.prototype.hasOwnProperty.call(overrides, "search")
        ? overrides.search
        : this.filters.search;
      const status = Object.prototype.hasOwnProperty.call(overrides, "status")
        ? overrides.status
        : this.filters.status;
      const statuses = Object.prototype.hasOwnProperty.call(
        overrides,
        "statuses"
      )
        ? this.parseQueryNumberList(overrides.statuses)
        : status
        ? []
        : this.routeStatusValues;
      const area = Object.prototype.hasOwnProperty.call(overrides, "area")
        ? overrides.area
        : this.filters.area;
      const page = Object.prototype.hasOwnProperty.call(overrides, "page")
        ? overrides.page
        : this.data.page;
      const company = Object.prototype.hasOwnProperty.call(overrides, "company")
        ? overrides.company
        : this.currentCompanyId;
      const query = {};

      if (company) {
        query.company = String(company);
      }
      if (search) {
        query.search = search;
      }
      if (status) {
        query.status = String(status);
      } else if (statuses.length > 1) {
        query.statuses = statuses.join(",");
      }
      if (area) {
        query.area = String(area);
      }
      if (page && page > 1) {
        query.page = String(page);
      }

      return query;
    },
    hasSameRouteQuery(query) {
      return ["company", "search", "status", "statuses", "area", "page"].every(
        (key) => {
          const currentValue =
            this.$route.query[key] === undefined
              ? null
              : String(this.$route.query[key]);
          const nextValue =
            query[key] === undefined ? null : String(query[key]);
          return currentValue === nextValue;
        }
      );
    },
    syncRouteQuery(overrides = {}) {
      const query = this.buildRouteQuery(overrides);

      if (this.hasSameRouteQuery(query)) {
        this.applyRouteState();
        return this.loadRisks();
      }

      return this.$router.replace({
        path: this.$route.path,
        query,
      });
    },
    mapUserOptions(users) {
      const scopedUsers = this.currentCompanyId
        ? users.filter((item) => {
            const companyId = this.normalizeQueryNumber(
              item.company?._id || item.company
            );
            return companyId === this.currentCompanyId;
          })
        : users;

      return scopedUsers.map((item) => ({
        value: item._id,
        text: item.fullname || `${item.name} ${item.surname || ""}`.trim(),
      }));
    },
    hasRiskPermission(permission) {
      const role = this.profile.role;
      if (role === "company-root" || role === "root") {
        return true;
      }

      if (parseInt(role) === role) {
        return Boolean(this.permissions?.risks?.[permission]);
      }

      return false;
    },
    async loadSupportData() {
      try {
        const [
          areasResponse,
          scopesResponse,
          projectsResponse,
          usersResponse,
          requirementsResponse,
        ] = await Promise.all([
          getGrcMetadata("areas", this.getRequestParams()),
          getGrcMetadata("scopes", this.getRequestParams()),
          getGrcMetadata("projects", this.getRequestParams()),
          getUsers({ page: 1, size: 200 }),
          getGrcRequirements(this.getRequestParams({ page: 1, size: 200 })),
        ]);

        this.areaOptions = areasResponse.data.map((item) => ({
          value: item._id,
          text: item.code ? `${item.code} - ${item.name}` : item.name,
          selectedText: item.name,
        }));
        this.scopeOptions = scopesResponse.data.map((item) => ({
          value: item._id,
          text: item.area?.name
            ? `${item.name} (${item.area.name})`
            : item.name,
          selectedText: item.name,
        }));
        this.projectOptions = projectsResponse.data.map((item) => ({
          value: item._id,
          text: item.code ? `${item.code} - ${item.name}` : item.name,
          selectedText: item.name,
        }));
        this.userOptions = this.mapUserOptions(usersResponse.data.docs || []);
        this.requirementOptions = (requirementsResponse.data.docs || []).map(
          (item) => ({
            value: item._id,
            text: item.title,
            selectedText: item.title,
          })
        );
      } catch (error) {
        this.showError(error, "Não foi possível carregar os dados auxiliares");
      }
    },
    async loadRisks() {
      this.loading = true;
      try {
        const response = await getGrcRisks(this.getListParams());
        this.data = response.data;
      } catch (error) {
        this.showError(error, "Não foi possível carregar os riscos");
      } finally {
        this.loading = false;
      }
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const response = await getGrcRisks(
          this.getListParams({
            page: 1,
            size: Math.max(this.data.total || this.data.limit || 1, 1),
          })
        );
        const rows = (response.data.docs || []).map((item) => [
          item._id,
          item.title,
          item.area ? item.area.name : "-",
          item.score,
          this.levelLabel(item.inherentLevel),
          this.statusLabel(item.status),
          this.treatmentLabel(item.treatmentStrategy),
          this.formatDate(item.nextReviewAt),
        ]);

        exportRowsToCsv(
          `grc_riscos_${new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:T]/g, "-")}.csv`,
          [
            "ID",
            "Risco",
            "Área",
            "Score",
            "Nível",
            "Status",
            "Tratamento",
            "Próxima revisão",
          ],
          rows
        );
      } catch (error) {
        this.showError(error, "Não foi possível exportar os riscos");
      } finally {
        this.exporting = false;
      }
    },
    applyFilters() {
      this.data.page = 1;
      this.syncRouteQuery({ page: 1 });
    },
    clearSearch() {
      this.filters.search = "";
      this.applyFilters();
    },
    changePage(page) {
      this.data.page = page;
      this.syncRouteQuery({ page });
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
      this.data.page = 1;
      if (this.$route.query.page) {
        this.syncRouteQuery({ page: 1 });
        return;
      }
      this.loadRisks();
    },
    openNew() {
      this.selectedRisk = null;
      this.showSidebar = true;
    },
    async openEdit(item) {
      try {
        const response = await getGrcRisk(item._id, this.getRequestParams());
        this.selectedRisk = response.data;
        this.showSidebar = true;
      } catch (error) {
        this.showError(error, "Não foi possível carregar o risco");
      }
    },
    onModalHidden() {
      this.selectedRisk = null;
    },
    async saveRisk(payload) {
      this.saving = true;
      try {
        const requestParams = this.getRequestParams();
        const data = this.getMutationPayload(payload);
        if (this.selectedRisk?._id) {
          await updateGrcRisk(this.selectedRisk._id, data, requestParams);
        } else {
          await createGrcRisk(data, requestParams);
        }

        this.$bvToast.toast("Risco salvo com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        this.showSidebar = false;
        await this.loadRisks();
      } catch (error) {
        this.showError(error, "Não foi possível salvar o risco");
      } finally {
        this.saving = false;
      }
    },
    async approve(item) {
      try {
        await approveGrcRisk(item._id, {}, this.getRequestParams());
        this.$bvToast.toast("Risco aprovado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadRisks();
      } catch (error) {
        this.showError(error, "Não foi possível aprovar o risco");
      }
    },
    async reject(item) {
      const reason = window.prompt("Informe o motivo da rejeição:");
      if (!reason) {
        return;
      }

      try {
        await rejectGrcRisk(item._id, { reason }, this.getRequestParams());
        this.$bvToast.toast("Risco rejeitado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadRisks();
      } catch (error) {
        this.showError(error, "Não foi possível rejeitar o risco");
      }
    },
    async remove(item) {
      const confirmed = window.confirm(
        `Deseja inativar o risco ${item.title}?`
      );
      if (!confirmed) {
        return;
      }

      try {
        await deleteGrcRisk(item._id, this.getRequestParams());
        this.$bvToast.toast("Risco inativado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadRisks();
      } catch (error) {
        this.showError(error, "Não foi possível inativar o risco");
      }
    },
    statusLabel(value) {
      const labels = {
        [STATUS.OPEN]: "Aberto",
        [STATUS.WAITING_APPROVAL]: "Aguardando aprovação",
        [STATUS.CLOSED]: "Fechado",
        [STATUS.IN_REVIEW]: "Em revisão",
      };
      return labels[value] || "-";
    },
    statusVariant(value) {
      const variants = {
        [STATUS.OPEN]: "danger",
        [STATUS.WAITING_APPROVAL]: "warning",
        [STATUS.CLOSED]: "success",
        [STATUS.IN_REVIEW]: "info",
      };
      return variants[value] || "secondary";
    },
    treatmentLabel(value) {
      const labels = {
        avoid: "Anular / Evitar",
        reduce: "Reduzir / Mitigar",
        share: "Compartilhar",
        transfer: "Transferir",
        accept: "Aceitar",
      };
      return labels[value] || "-";
    },
    levelLabel(value) {
      const labels = {
        insignificant: "Insignificante",
        low: "Baixo",
        medium: "Médio",
        high: "Alto",
        catastrophic: "Catastrófico",
      };
      return labels[value] || "-";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleDateString("pt-BR");
    },
    getApprovalStatus(item) {
      return item && item.approval ? item.approval.status : null;
    },
    showError(error, fallback) {
      const message =
        error?.response?.data || error?.response?.statusText || fallback;
      this.$bvToast.toast(message, {
        title: "Erro",
        toastClass: "custom-toast custom-toast-error",
        variant: "light",
        solid: true,
        autoHideDelay: 10000,
      });
    },
  },
};
</script>

<style scoped>
@import "./grc-list.scss";
</style>
